import React from "react";
import { motion } from "framer-motion";
import { IconExternalLink, IconLayers } from "../components/icons/ThinIcons";
import { useSiteConfig, useUiLabels } from "../context/LocaleContext";
import { useMicrolinkPreview } from "../hooks/useMicrolinkPreview";

interface ProjectCardProps {
  title: string;
  description: string;
  url?: string;
  imgUrl?: string;
  technologies?: string[];
  clientName?: string;
  index: number;
  labels: {
    previewUnavailable: string;
    previewLiveUnavailable: string;
    openSiteAria: string;
    visitSite: string;
    cardAlt: string;
  };
}

const easeFluid = [0.32, 0.72, 0, 1] as const;

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  description,
  url,
  imgUrl,
  technologies,
  clientName,
  index,
  labels,
}) => {
  const { imageUrl, siteTitle, loading, error } = useMicrolinkPreview(url);
  const displaySrc = !error && imageUrl ? imageUrl : imgUrl;
  const showSkeleton = Boolean(url) && loading && !displaySrc;

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 28 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.55,
        delay: index * 0.07,
        ease: easeFluid,
      }}
      className="group bezel-outer"
    >
      <div className="bezel-inner overflow-hidden shadow-[0_28px_90px_-48px_rgba(124,92,255,0.35)] transition-[box-shadow] duration-700 ease-fluid group-hover:shadow-[0_32px_100px_-40px_rgba(16,185,129,0.2)]">
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#0a0a0c]">
          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            }}
          />

          {showSkeleton && <div className="absolute inset-0 loading-shimmer" />}

          {displaySrc && !showSkeleton && (
            <motion.img
              src={displaySrc}
              alt={`${labels.cardAlt} ${title}`}
              className="h-full w-full object-cover object-top transition-transform duration-700 ease-fluid group-hover:scale-[1.03]"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                const t = e.currentTarget;
                if (imgUrl && t.src !== imgUrl) t.src = imgUrl;
              }}
            />
          )}

          {!url && !displaySrc && !showSkeleton && (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-violet-950/40 to-[#050505]">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-600">{labels.previewUnavailable}</span>
            </div>
          )}

          {error && url && !displaySrc && !showSkeleton && (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-black/70 px-4 text-center">
              <p className="text-xs text-emerald-200/90">{labels.previewLiveUnavailable}</p>
              {imgUrl && (
                <img src={imgUrl} alt="" className="max-h-[60%] max-w-[90%] rounded-xl ring-1 ring-white/10" loading="lazy" />
              )}
            </div>
          )}

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#08080a] via-[#08080a]/50 to-transparent" />

          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white opacity-0 ring-1 ring-white/[0.12] transition-[opacity,transform,background-color] duration-700 ease-fluid hover:bg-emerald-500/30 group-hover:opacity-100"
              aria-label={`${labels.openSiteAria}: ${title}`}
            >
              <IconExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>

        <div className="relative p-6 md:p-7">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-xl font-bold tracking-tight text-white md:text-2xl">{title}</h2>
              {clientName && <p className="mt-1 font-body text-sm text-emerald-200/85">{clientName}</p>}
              {siteTitle && url && <p className="mt-1 line-clamp-1 font-mono text-xs text-zinc-500">{siteTitle}</p>}
            </div>
          </div>

          <p className="font-body text-sm leading-relaxed text-zinc-400 md:text-[15px]">{description}</p>

          {technologies && technologies.length > 0 && (
            <ul className="mt-5 flex flex-wrap gap-2">
              {technologies.map((tech) => (
                <li
                  key={tech}
                  className="rounded-full bg-white/[0.04] px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-emerald-100/90 ring-1 ring-white/[0.08]"
                >
                  {tech}
                </li>
              ))}
            </ul>
          )}

          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="group/link mt-6 inline-flex items-center gap-2 font-body text-sm font-semibold text-emerald-200/95 transition-[color,transform] duration-700 ease-fluid hover:text-white active:scale-[0.99]"
            >
              {labels.visitSite}
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.06] ring-1 ring-white/[0.08] transition-[transform] duration-700 ease-fluid group-hover/link:translate-x-0.5 group-hover/link:-translate-y-px">
                <IconExternalLink className="h-3.5 w-3.5 opacity-90" />
              </span>
            </a>
          )}
        </div>
      </div>
    </motion.article>
  );
};

const ProjectsPage: React.FC = () => {
  const config = useSiteConfig();
  const ui = useUiLabels();
  const projects = config.projects;
  const pl = ui.projects;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.65, ease: easeFluid }}
      className="flex w-full flex-col px-4 pb-24 pt-20 md:px-10 md:pt-28"
    >
      <div className="mx-auto w-full max-w-6xl">
        <header className="mb-14 md:mb-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: easeFluid }}>
            <div className="mb-6 inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-200/90 ring-1 ring-emerald-400/25">
              <IconLayers className="h-3.5 w-3.5" />
              {pl.badge}
            </div>
            <h1 className="font-heading text-4xl font-extrabold tracking-tight text-white md:text-6xl md:leading-[1.05]">{pl.title}</h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-zinc-400 md:text-lg">{pl.intro}</p>
            <div className="mt-8 h-px max-w-md bg-gradient-to-r from-emerald-400/50 via-violet-400/30 to-transparent" />
          </motion.div>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          {projects.map((project, i) => {
            const span = i % 3 === 0 ? "lg:col-span-7" : i % 3 === 1 ? "lg:col-span-5" : "lg:col-span-12";
            return (
              <div key={`${project.projectId}-${project.title}-${i}`} className={span}>
                <ProjectCard
                  index={i}
                  title={project.title}
                  description={project.description}
                  url={project.url}
                  imgUrl={project.imgUrl}
                  technologies={project.technologies}
                  clientName={project.clientName}
                  labels={{
                    previewUnavailable: pl.previewUnavailable,
                    previewLiveUnavailable: pl.previewLiveUnavailable,
                    openSiteAria: pl.openSiteAria,
                    visitSite: pl.visitSite,
                    cardAlt: pl.cardAlt,
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectsPage;
